<template>
   <Head>
      <title>{{ $t("meta.login.title") }}</title>
      <meta name="description" :content="$t('meta.login.content')" />
   </Head>
   <ClientOnly>
      <AuthLayout :heading="$t('authentication.welcome_back')">
         <template v-slot:form>
            <div v-if="clientSettings.googleSignInEnabled">
               <!-- login with google button -->
               <GoogleLoginButton @google-click="onLoginWithGoogle">
                  {{ $t("authentication.signin_google") }}
               </GoogleLoginButton>

               <!-- OAuth error message -->
               <div v-if="oAuthErrorMessage" class="oauth-err-block">
                  <SnAlert
                     data-test="oauth-error"
                     data-cy="oauth-error"
                     :message="oAuthErrorMessage"
                     id="oauth-validation-message"
                     theme="danger"
                     size="small"
                     class="validation-alert"
                     icon="warning-circle-filled"
                  />
               </div>

               <div class="signup-alt-divider">
                  <div class="divide"></div>
                  <span>{{ $t("authentication.or") }}</span>
               </div>
            </div>

            <!-- form: login inputs (email + password) -->
            <form class="email-pw-form" onsubmit="event.preventDefault();">
               <!-- email field -->
               <SnTextLine
                  id="email"
                  v-model.trim="state.email"
                  data-cy="login-email-input"
                  redBorderError
                  iconError
                  :label="$t('validation.email.value')"
                  :placeholder="$t('validation.email.placeholder')"
                  :error="emailHelperData"
                  size="large"
               />

               <div class="password-input-block">
                  <div class="password-additional-labels">
                     <!-- password visual-only label -->
                     <span aria-hidden="true">{{ $t("validation.password.value") }}</span>
                     <!-- forgot password -->
                     <router-link
                        :to="{ name: 'password-lost', params: { locale: $route.params?.locale } }"
                        data-cy="to-register"
                        class="forgot-password"
                     >
                        {{ $t("authentication.forgot_password") }}
                     </router-link>
                  </div>
                  <!-- password field -->
                  <SnTextLine
                     id="password"
                     v-model.trim="state.password"
                     data-cy="login-password-input"
                     type="password"
                     redBorderError
                     iconError
                     :showLabel="false"
                     :label="$t('validation.password.value')"
                     :autocomplete="true"
                     :error="passwordHelperData"
                     size="large"
                     @keyup.enter="onLogin"
                  />
               </div>

               <!-- login button -->
               <LoadingButton
                  :isLoading="state.isLoading"
                  class="submit-btn"
                  data-cy="login-button"
                  data-test="login-button"
                  @click="onLogin"
               >
                  {{ $t("authentication.log_me_in") }}
               </LoadingButton>
            </form>
         </template>

         <template v-slot:bottom>
            <div v-if="clientSettings.signup.isActive">
               <span>{{ $t("authentication.no_account") }}</span>
               <router-link
                  :to="{
                     name: 'signup',
                     params: { locale: $route.params?.locale },
                     query: { redirect: $route.query?.redirect },
                  }"
                  data-cy="to-register"
                  class="text-bolder mx-1 inline-block border-b text-brand-dark"
               >
                  {{ $t("authentication.signup") }}
               </router-link>
            </div>
         </template>
      </AuthLayout>
   </ClientOnly>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { Head } from "@unhead/vue/components";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { useRouter, useRoute } from "vue-router";
import { useToastKey } from "@/plugins/useToast";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { addLocaleToRedirectPath } from "@/router/routes-config";
import { useUserStore } from "@/stores/userStore";
import SnTextLine from "@sano-ui-library/src/ui/Form/SnTextLine/SnTextLine.vue";
import SnAlert from "@sano-ui-library/src/ui/SnAlert/SnAlert.vue";

const $router = useRouter();
const $route = useRoute();

const useToast = inject(useToastKey);

const i18n = useI18n();

const clientSettings = inject(useClientSettingsKey);
const userStore = useUserStore();

interface QueryParams {
   redirect?: string; // ex /account
   params?: any; // any other parameters in the query string
}

// data & requests
// -----------------------------------------
const state = reactive({
   isLoading: false,
   password: null,
   email: null,
   queryParams: $route.query,
});

const $v: any = useVuelidate(
   {
      email: { required, email },
      password: { required },
   },
   state
);

// computed
// -----------------------------------------
const emailHelperData = computed(() => {
   if ($v.value.email.$errors.length === 0) return null;

   if ($v.value.email.$errors[0].$validator === "required") {
      return i18n.t("validation.email.required");
   } else if ($v.value.email.$errors[0].$validator === "email") {
      return i18n.t("validation.email.invalid");
   }
   return null;
});

const passwordHelperData = computed(() => {
   if ($v.value.password.$errors.length === 0) return null;

   if ($v.value.password.$errors[0].$validator === "required") {
      return i18n.t("validation.password.required");
   }
   return null;
});

const oAuthErrorMessage = computed(() => {
   if ($route.query?.error_code) {
      switch ($route.query.error_code) {
         case "oauth_account_not_found":
            return i18n.t("validation.oauth.account_not_found");
         case "oauth_account_not_linked":
            return i18n.t("validation.oauth.account_not_linked");
         default:
            return i18n.t("validation.oauth.error");
      }
   }
   return null;
});

onMounted(() => {
   // if login_required is in the query parameters, show the appropriate warning.
   if ($route.query.login_required !== undefined) {
      useToast({ text: i18n.t("authentication.login_required"), type: "warning" });
   }
});

// methods
// -----------------------------------------
const onLoginWithGoogle = async () => {
   if (!import.meta.env.SSR) {
      let fullApiPath = "";
      // pre-rendering OR local development (requires full path because the server port is different)
      if (import.meta.env.MODE !== "production") {
         fullApiPath = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_API_PATH}`;
      }
      // production (API endpoint is the same as the base URL)
      else {
         fullApiPath = `${import.meta.env.VITE_APP_API_PATH}`;
      }

      let redirect = $route.query.redirect as string;
      let gLoginParams = "client=participant&action=sign-in";

      if (!redirect) redirect = "/home";
      gLoginParams += `&redirect_path=${addLocaleToRedirectPath(redirect, i18n)}`;

      window.location.assign(`${fullApiPath}/oauth/authorize?${gLoginParams}`);
   }
};

const onLogin = async () => {
   // Because enter may trigger this method, we need to re-check the form
   $v.value.$touch();

   if ($v.value.$error) {
      return;
   }

   // form is valid
   const ls_privacy_services = null;
   state.isLoading = true;
   try {
      const response = await userStore.login(state.email, state.password, state.queryParams, {}, ls_privacy_services);
      const codeNeeded = response.data.codeSent;
      onLoginSuccess(codeNeeded);
   } catch (error: any) {
      if (error.response?.data?.error === "too_many_failed_attempts") {
         useToast({ text: i18n.t("validation.too_many_failed_attempts"), type: "error" });
      } else if (error.response?.data?.error === "invalid_credentials") {
         useToast({ text: i18n.t("validation.invalid_credentials"), type: "error" });
      } else if (error.response?.data?.error === "password_expired") {
         useToast({ text: i18n.t("validation.password.password_expired"), type: "error" });
      } else {
         console.error("Error while loggin in:", error);
      }
      state.isLoading = false;
   }
};

const onLoginSuccess = (codeNeeded) => {
   // if a device code is needed, go to the device code page (keep params intact)
   if (codeNeeded) {
      $router.push({ name: "device-code", query: $route.query, params: { locale: $route.params?.locale } });
   }
   // we have a device code > redirect to the required logged in page (keeping params intact)
   else {
      const { redirect, ...params }: QueryParams = $route.query;
      // if the user was directed to a flow (likely from a logged out study page), redirect them there
      if (redirect) {
         $router.push({ path: addLocaleToRedirectPath(redirect, i18n), query: params });
      }
      // or redirect if someone was trying to access a particular page before being sent to login (home is default)
      else {
         $router.push({ name: "home", query: params, params: { locale: $route.params?.locale } });
      }
   }
};
</script>

<style scoped lang="css">
.signup-alt-divider {
   display: flex;
   justify-content: center;
   margin: var(--size-base-24) 0;
   position: relative;
}

.signup-alt-divider span {
   background-color: var(--white);
   display: block;
   font-size: var(--size-text-small);
   color: var(--grey-80);
   font-weight: var(--weight-base);
   line-height: 1.2;
   position: relative;
   padding: 0 var(--size-base-16);
}

.signup-alt-divider .divide {
   position: absolute;
   top: 50%;
   left: 0;
   right: 0;
   height: 1px;
   background-color: var(--grey-20);
}

.email-pw-form {
   font-weight: var(--weight-base);
   color: var(--grey-80);
}

.password-input-block {
   margin-block-start: var(--size-base-24);
   position: relative;
   color: var(--grey-80);
}

.password-additional-labels {
   display: flex;
   flex-wrap: wrap;
   align-items: center;
   justify-content: space-between;
   column-gap: var(--size-base-16);
   margin-block-end: var(--size-base-8);
   line-height: 1.2;
}

.submit-btn {
   margin-block-start: var(--size-base-32);
   margin-block-end: var(--size-base-32);
   width: 100%;
}

.oauth-err-block {
   padding-block-start: var(--size-base-8);
   text-align: left;
   font-weight: var(--weight-base);
}

.holder {
   min-height: 510px;
}
</style>
